/**
 * PDP Bundle - Instant Buy Now
 * Custom element: <pdp-instant-bundle>
 * Renders a configurable product bundle below the PDP main info and lets the
 * customer skip the cart entirely: clicking "Buy Bundle Now" clears the cart,
 * adds the selected line items, then redirects straight to /checkout.
 */
(function () {
  'use strict';

  // Some third-party pixel/tracking apps patch window.fetch globally to
  // observe cart requests; if that patch is broken (e.g. it awaits its own
  // failing pixel call before forwarding to the real fetch), our own
  // /cart/add.js call can hang forever with no error, leaving the button
  // stuck on "Processing..." indefinitely. Race it against a timeout so we
  // always resolve/reject and can show the user something went wrong.
  function fetchWithTimeout(url, options, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        reject(new Error('Request timed out.'));
      }, timeoutMs);

      fetch(url, options).then(
        function (response) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          resolve(response);
        },
        function (error) {
          if (settled) return;
          settled = true;
          window.clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  // The theme's other cart-polling widgets (gift-with-purchase, quantity
  // selectors) fire bursts of /cart.js and /cart/update.js calls on their
  // own; when one of those bursts overlaps this flow's own /cart.js and
  // ?sections= refetches, Shopify's Storefront API 429s them. A bare fetch
  // treats a 429 response as a normal resolved promise (not a rejection),
  // so an unchecked res.json() either throws on the non-JSON body or
  // silently parses garbage - both were being swallowed by a blind .catch,
  // which is why the badge/drawer update would sometimes just never
  // happen for that add-to-cart. Retry with backoff instead of giving up
  // after one shot.
  function fetchJsonWithRetry(url, options, attemptsLeft, delayMs) {
    return fetchWithTimeout(url, options, 10000)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .catch(function (err) {
        if (attemptsLeft <= 1) throw err;
        return new Promise(function (resolve) {
          window.setTimeout(resolve, delayMs);
        }).then(function () {
          return fetchJsonWithRetry(url, options, attemptsLeft - 1, delayMs * 1.6);
        });
      });
  }

  function formatMoney(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) cents = 0;
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function trackEvent(name, payload) {
    payload = payload || {};
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name }, payload));
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, payload);
    }
  }

  // The main PDP product is a bundle item like any other now (its own
  // checkbox, its own data-price/data-variant-id), so it matches this
  // selector alongside every recommended card.
  var CARD_SELECTOR = '.pib-card[data-role="main"], .pib-card[data-role="recommended"]';

  var PdpInstantBundle = /** @class */ (function () {
    function PdpInstantBundle(el) {
      this.el = el;
      this.moneyFormat = el.dataset.moneyFormat || '${{amount}}';
      this.discountType = el.dataset.discountType || 'percentage';
      this.discountPercentage = parseFloat(el.dataset.discountPercentage || '0');
      this.discountFixed = parseInt(el.dataset.discountFixed || '0', 10);
      this.percentageOfferLabel = el.dataset.percentageOfferLabel || '';
      this.fixedOfferLabel = el.dataset.fixedOfferLabel || '';
      this.bxgyBuyQuantity = parseInt(el.dataset.bxgyBuyQuantity || '2', 10);
      this.bxgyGetQuantity = parseInt(el.dataset.bxgyGetQuantity || '2', 10);
      this.bxgyOfferLabel = el.dataset.bxgyOfferLabel || '';
      this.discountRequiresAddon = el.dataset.discountRequiresAddon === 'true';
      this.discountCode = el.dataset.discountCode || '';
      this.tiers = [];
      try {
        this.tiers = JSON.parse(el.dataset.tiers || '[]');
      } catch (e) {
        this.tiers = [];
      }
      this.tiers.sort(function (a, b) {
        return a.min - b.min;
      });

      this.ctaMode = el.dataset.ctaMode === 'add_to_cart' ? 'add_to_cart' : 'buy_now';

      this.cards = Array.prototype.slice.call(el.querySelectorAll(CARD_SELECTOR));
      this.mainCard = el.querySelector('.pib-card[data-role="main"]');
      this.buyButtons = Array.prototype.slice.call(el.querySelectorAll('[data-role="buy-button"]'));
      this.statusRegions = Array.prototype.slice.call(el.querySelectorAll('[data-role="status"]'));
      this.summaryTargets = {
        subtotal: el.querySelectorAll('[data-bind="subtotal"]'),
        discount: el.querySelectorAll('[data-bind="discount"]'),
        savingsPercent: el.querySelectorAll('[data-bind="savings-percent"]'),
        final: el.querySelectorAll('[data-bind="final"]'),
        discountLabel: el.querySelectorAll('[data-bind="discount-label"]'),
      };
      this.savingsRow = el.querySelectorAll('[data-role="savings-row"]');
      this.countLabels = el.querySelectorAll('[data-role="count-label"]');
      this.itemCountNumbers = el.querySelectorAll('[data-bind="item-count-number"]');

      this.cardsRow = el.querySelector('[data-role="cards-row"]');
      this.scrollHint = el.querySelector('[data-role="scroll-hint"]');

      this.displayedFinal = this.mainCard ? parseInt(this.mainCard.dataset.price || '0', 10) : 0;
      this.isSubmitting = false;
      this.viewedTracked = false;

      this.bindEvents();
      this.disableCardNavigation();
      this.preventControlClicksFromOpeningModal();
      this.relocateFreeBadges();
      this.recalculate(true);
      this.observeVisibility();
      this.bindScrollHint();
      this.bindQuickViewModal();

      document.addEventListener('variant:change', this.onMainVariantChange.bind(this));
    }

    PdpInstantBundle.prototype.observeVisibility = function () {
      var self = this;
      if (!('IntersectionObserver' in window)) return;
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !self.viewedTracked) {
            self.viewedTracked = true;
            trackEvent('bundle_viewed', { bundle_value: self.displayedFinal / 100 });
            observer.disconnect();
          }
        });
      }, { threshold: 0.4 });
      observer.observe(this.el);
    };

    PdpInstantBundle.prototype.bindScrollHint = function () {
      var self = this;
      if (!this.cardsRow || !this.scrollHint) return;

      var ticking = false;
      function onScroll() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          self.updateScrollHint();
          ticking = false;
        });
      }

      this.cardsRow.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);

      if ('ResizeObserver' in window) {
        new ResizeObserver(onScroll).observe(this.cardsRow);
      }

      this.scrollHint.addEventListener('click', function () {
        self.scrollCardsForward();
      });

      this.updateScrollHint();
    };

    PdpInstantBundle.prototype.updateScrollHint = function () {
      var row = this.cardsRow;
      var hint = this.scrollHint;
      if (!row || !hint) return;

      var hasOverflow = row.scrollWidth > row.clientWidth + 1;
      if (!hasOverflow) {
        hint.classList.remove('pib-scroll-hint--visible');
        hint.disabled = true;
        return;
      }

      var atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 4;
      hint.classList.toggle('pib-scroll-hint--visible', !atEnd);
      hint.disabled = atEnd;
    };

    PdpInstantBundle.prototype.scrollCardsForward = function () {
      var row = this.cardsRow;
      if (!row) return;

      var reducedMotion = !!(window.theme && window.theme.config && window.theme.config.motionReduced);
      var step = row.clientWidth * 0.9;

      row.scrollBy({ left: step, behavior: reducedMotion ? 'auto' : 'smooth' });
    };

    // Quick view is the shared product-info-modal component (see
    // assets/product-info-modal.js + snippets/product-info-modal.liquid),
    // the same one Build Box uses — this bundle only reacts to the
    // "add" click for cards rendered with context: 'bundle', by
    // checking the matching card's own checkbox through its existing
    // change listener so selection/pricing logic isn't duplicated here.
    PdpInstantBundle.prototype.bindQuickViewModal = function () {
      var self = this;

      document.addEventListener('product-info-modal:atc', function (event) {
        var detail = event.detail || {};
        if (detail.context !== 'bundle') return;

        var card = self.cards.filter(function (c) {
          return c.dataset.productId === String(detail.productId);
        })[0];
        if (!card) return;

        var checkbox = card.querySelector('[data-role="checkbox"]');
        if (!checkbox || checkbox.disabled || checkbox.checked) return;

        checkbox.checked = true;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));

        trackEvent('bundle_quick_view_add', {
          product_id: detail.productId,
          product_name: detail.title || '',
        });
      });
    };

    PdpInstantBundle.prototype.onMainVariantChange = function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant || !this.mainCard) return;

      var available = !!variant.available;
      this.mainCard.dataset.variantId = String(variant.id);
      this.mainCard.dataset.price = variant.price;
      this.mainCard.dataset.compareAtPrice = variant.compare_at_price || '';
      this.mainCard.dataset.available = available ? 'true' : 'false';

      var onSale = !!(variant.compare_at_price && variant.compare_at_price > variant.price);
      var priceEl = this.el.querySelector('[data-bind="main-price"]');
      var compareEl = this.el.querySelector('[data-bind="main-compare-price"]');
      if (priceEl) priceEl.textContent = formatMoney(variant.price, this.moneyFormat);
      if (compareEl) {
        compareEl.hidden = !onSale;
        if (onSale) compareEl.textContent = formatMoney(variant.compare_at_price, this.moneyFormat);
        var priceWrap = compareEl.closest('.price');
        if (priceWrap) priceWrap.classList.toggle('price--on-sale', onSale);
      }

      var checkbox = this.mainCard.querySelector('[data-role="checkbox"]');
      if (!available) {
        if (checkbox) {
          checkbox.checked = false;
          checkbox.disabled = true;
        }
        this.mainCard.classList.remove('pib-card--selected');
      } else if (checkbox) {
        checkbox.disabled = false;
      }

      this.recalculate();
    };

    PdpInstantBundle.prototype.bindEvents = function () {
      var self = this;

      this.cards.forEach(function (card) {
        var checkbox = card.querySelector('[data-role="checkbox"]');
        var select = card.querySelector('[data-role="variant-select"]');

        if (checkbox) {
          checkbox.addEventListener('change', function () {
            self.onToggleCard(card, checkbox.checked);
          });
        }

        if (select) {
          select.addEventListener('change', function () {
            self.onVariantSelect(card, select);
          });
        }
      });

      this.buyButtons.forEach(function (button) {
        button.addEventListener('click', function (event) {
          event.preventDefault();
          self.handleCtaClick(event);
        });
      });
    };

    // The reused 'product-card' snippet renders its own image and title
    // links to the PDP (see snippets/product-card.liquid). Inside the
    // bundle, navigating away mid-selection is unwanted — only the
    // checkbox/variant-select/quick-view should be interactive. Rather
    // than editing the shared, sitewide snippet, neutralize just the
    // anchors scoped to .pib-inner-card: drop them from the tab order and
    // swallow their click (covers both mouse clicks and keyboard-Enter
    // activation, which also dispatches a click event).
    PdpInstantBundle.prototype.disableCardNavigation = function () {
      this.cards.forEach(function (card) {
        var links = card.querySelectorAll('.pib-inner-card a[href]');
        links.forEach(function (link) {
          link.setAttribute('tabindex', '-1');
          link.setAttribute('aria-hidden', 'true');
          link.addEventListener('click', function (event) {
            event.preventDefault();
          });
        });
      });
    };

    // Every card has data-quick-view-card (see the markup), which the
    // shared assets/product-info-modal.js already wires up to open that
    // product's quick-view modal on any click inside the card — except
    // clicks on a <button> or <a>, which it already skips (that's what
    // keeps the quick-view eye icon working normally). The checkbox and
    // variant <select> aren't buttons or links, so without this they'd
    // also bubble up and open the modal when the user just meant to
    // select the product or change its variant. Stopping propagation
    // right at these controls (not by touching the shared modal script)
    // keeps that from happening.
    PdpInstantBundle.prototype.preventControlClicksFromOpeningModal = function () {
      this.cards.forEach(function (card) {
        var toggleLabel = card.querySelector('.pib-toggle');
        if (toggleLabel) {
          toggleLabel.addEventListener('click', function (event) {
            event.stopPropagation();
          });
        }

        var variantSelect = card.querySelector('.pib-variant-select');
        if (variantSelect) {
          variantSelect.addEventListener('click', function (event) {
            event.stopPropagation();
          });
        }
      });
    };

    // The FREE badge (data-role="free-badge") needs to sit on top of the
    // product image so a free card is never taller than a full-price one,
    // but it's rendered as a sibling of the shared product-card snippet's
    // markup, not a child of its image wrapper — CSS position:absolute
    // can only resolve against a DOM ancestor, not a same-level sibling.
    // Rather than editing the shared, sitewide product-card.liquid to add
    // a hook, relocate the already-rendered badge into its real
    // .product-card__media (already position:relative there) once at
    // init; only its hidden attribute changes after that, in
    // updateFreeBadges(), so this only needs to run once per card.
    PdpInstantBundle.prototype.relocateFreeBadges = function () {
      this.cards.forEach(function (card) {
        var badge = card.querySelector('[data-role="free-badge"]');
        var mediaWrap = card.querySelector('.product-card__media');
        if (badge && mediaWrap) {
          mediaWrap.appendChild(badge);
        }
      });
    };

    PdpInstantBundle.prototype.isShiprocketActive = function () {
      var indiaTimezones = ['Asia/Kolkata', 'Asia/Calcutta'];
      try {
        var timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return (
          indiaTimezones.indexOf(timezone) !== -1 &&
          typeof window.shiprocketCheckoutEvents !== 'undefined' &&
          typeof window.shiprocketCheckoutEvents.buyCart === 'function'
        );
      } catch (e) {
        return false;
      }
    };

    PdpInstantBundle.prototype.onVariantSelect = function (card, select) {
      var option = select.options[select.selectedIndex];
      var available = option.dataset.available === 'true';

      card.dataset.variantId = option.value;
      card.dataset.price = option.dataset.price;
      card.dataset.compareAtPrice = option.dataset.compareAtPrice || '';
      card.dataset.available = available ? 'true' : 'false';

      var priceEl = card.querySelector('[data-role="card-price"]');
      var compareEl = card.querySelector('[data-role="card-compare-price"]');
      var onSale = !!(option.dataset.compareAtPrice && parseInt(option.dataset.compareAtPrice, 10) > parseInt(option.dataset.price, 10));
      if (priceEl) priceEl.textContent = formatMoney(parseInt(option.dataset.price, 10), this.moneyFormat);
      if (compareEl) {
        if (onSale) {
          compareEl.textContent = formatMoney(parseInt(option.dataset.compareAtPrice, 10), this.moneyFormat);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
        var priceWrap = compareEl.closest('.price');
        if (priceWrap) priceWrap.classList.toggle('price--on-sale', onSale);
      }

      var checkbox = card.querySelector('[data-role="checkbox"]');
      var outOfStockEl = card.querySelector('[data-role="out-of-stock"]');

      if (!available) {
        if (checkbox) {
          checkbox.checked = false;
          checkbox.disabled = true;
        }
        card.classList.add('pib-card--unavailable');
        if (outOfStockEl) outOfStockEl.hidden = false;
      } else {
        if (checkbox) checkbox.disabled = false;
        card.classList.remove('pib-card--unavailable');
        if (outOfStockEl) outOfStockEl.hidden = true;
      }

      this.recalculate();
    };

    PdpInstantBundle.prototype.onToggleCard = function (card, checked) {
      card.classList.toggle('pib-card--selected', checked);
      var title = card.dataset.title || '';
      var productId = card.dataset.productId || '';
      var price = parseInt(card.dataset.price || '0', 10) / 100;

      trackEvent(checked ? 'bundle_product_selected' : 'bundle_product_deselected', {
        product_id: productId,
        product_name: title,
        price: price,
      });

      this.recalculate();
    };

    PdpInstantBundle.prototype.getSelectedCards = function () {
      return this.cards.filter(function (card) {
        var checkbox = card.querySelector('[data-role="checkbox"]');
        return checkbox && checkbox.checked && card.dataset.available !== 'false';
      });
    };

    // "Buy X Get Y Free" is a self-contained group discount (unlike
    // percentage/fixed/tiered) — it isn't a flat rate applied to the
    // subtotal, it's an allocation: whichever selected items are the
    // cheapest in each complete group become fully free, mirroring how
    // Shopify's own Buy X Get Y automatic discount actually applies at
    // checkout. Returns which variant ids are free (freeVariantIds) so
    // recalculate() can show a FREE badge only on those specific cards
    // instead of a flat percentage on every card.
    PdpInstantBundle.prototype.computeBxgyDiscount = function (selectedCards) {
      var groupSize = this.bxgyBuyQuantity + this.bxgyGetQuantity;
      if (groupSize <= 0) return { amount: 0, label: '', freeVariantIds: [] };

      var completeGroups = Math.floor(selectedCards.length / groupSize);
      var freeCount = completeGroups * this.bxgyGetQuantity;
      if (freeCount <= 0) return { amount: 0, label: '', freeVariantIds: [] };

      // Cheapest-first, one complete group at a time: Shopify's Buy X Get
      // Y automatic discount gives the free treatment to the lowest-priced
      // qualifying items, so the 2 highest-priced items of a 4-item
      // "Buy 2 Get 2" selection stay full price and the 2 cheapest go free.
      var sortedByPrice = selectedCards.slice().sort(function (a, b) {
        return parseInt(a.dataset.price || '0', 10) - parseInt(b.dataset.price || '0', 10);
      });
      var freeCards = sortedByPrice.slice(0, freeCount);
      var amount = freeCards.reduce(function (sum, card) {
        return sum + parseInt(card.dataset.price || '0', 10);
      }, 0);

      var label = this.bxgyOfferLabel || ('Buy ' + this.bxgyBuyQuantity + ' Get ' + this.bxgyGetQuantity + ' Free');

      return {
        amount: amount,
        label: label,
        freeVariantIds: freeCards.map(function (card) { return card.dataset.variantId; }),
      };
    };

    PdpInstantBundle.prototype.computeDiscount = function (subtotal, itemCount, addonCount, selectedCards) {
      if (this.discountType === 'bxgy') {
        return this.computeBxgyDiscount(selectedCards || []);
      }

      if (this.discountRequiresAddon && addonCount < 1) {
        return { amount: 0, label: '' };
      }

      if (this.discountType === 'percentage') {
        var pct = this.discountPercentage;
        return { amount: Math.round(subtotal * (pct / 100)), label: pct + '% off' };
      }

      if (this.discountType === 'fixed') {
        var fixed = Math.min(this.discountFixed, subtotal);
        return { amount: fixed, label: formatMoney(this.discountFixed, this.moneyFormat) + ' off' };
      }

      if (this.discountType === 'tiered') {
        var applicable = null;
        for (var i = 0; i < this.tiers.length; i++) {
          if (itemCount >= this.tiers[i].min) applicable = this.tiers[i];
        }
        if (!applicable) return { amount: 0, label: '' };
        return { amount: Math.round(subtotal * (applicable.pct / 100)), label: applicable.pct + '% off' };
      }

      // "Buy X at a fixed price" (e.g. "Buy 2 @699"): unlike the
      // percentage tiers above, each threshold sets the bundle's final
      // total directly rather than a rate applied to the subtotal — so
      // the discount amount is just whatever's left of the subtotal
      // once that fixed price is reached.
      if (this.discountType === 'tiered_price') {
        var applicablePrice = null;
        for (var j = 0; j < this.tiers.length; j++) {
          if (itemCount >= this.tiers[j].min) applicablePrice = this.tiers[j];
        }
        if (!applicablePrice || !applicablePrice.price) return { amount: 0, label: '' };
        var priceLabel = applicablePrice.label || ('Buy ' + applicablePrice.min + ' at ' + formatMoney(applicablePrice.price, this.moneyFormat));
        return { amount: Math.max(subtotal - applicablePrice.price, 0), label: priceLabel };
      }

      return { amount: 0, label: '' };
    };

    PdpInstantBundle.prototype.computeUpsellMessage = function (itemCount, addonCount) {
      if (this.discountType === 'bxgy') {
        var groupSize = this.bxgyBuyQuantity + this.bxgyGetQuantity;
        if (itemCount >= groupSize) return '';
        var bxgyOffer = this.bxgyOfferLabel || ('Buy ' + this.bxgyBuyQuantity + ' Get ' + this.bxgyGetQuantity + ' Free');
        return 'Select ' + groupSize + ' product' + (groupSize === 1 ? '' : 's') + ' to unlock ' + bxgyOffer;
      }

      if (this.discountType === 'tiered') {
        var nextTier = null;
        for (var i = 0; i < this.tiers.length; i++) {
          if (this.tiers[i].min > itemCount) {
            nextTier = this.tiers[i];
            break;
          }
        }
        if (!nextTier) return '';
        var remaining = nextTier.min - itemCount;
        var tierOffer = nextTier.label || (nextTier.pct + '% off');
        return 'Add ' + remaining + ' more product' + (remaining === 1 ? '' : 's') + ' to unlock ' + tierOffer;
      }

      if (this.discountType === 'tiered_price') {
        var nextPriceTier = null;
        for (var k = 0; k < this.tiers.length; k++) {
          if (this.tiers[k].min > itemCount) {
            nextPriceTier = this.tiers[k];
            break;
          }
        }
        if (!nextPriceTier) return '';
        var remainingPrice = nextPriceTier.min - itemCount;
        var priceTierOffer = nextPriceTier.label || ('Buy ' + nextPriceTier.min + ' at ' + formatMoney(nextPriceTier.price, this.moneyFormat));
        return 'Add ' + remainingPrice + ' more product' + (remainingPrice === 1 ? '' : 's') + ' to unlock ' + priceTierOffer;
      }

      if (this.discountRequiresAddon && addonCount < 1) {
        if (this.discountType === 'percentage') {
          var percentageOffer = this.percentageOfferLabel || (this.discountPercentage + '% off');
          return 'Add 1 product to unlock ' + percentageOffer;
        }
        if (this.discountType === 'fixed') {
          var fixedOffer = this.fixedOfferLabel || (formatMoney(this.discountFixed, this.moneyFormat) + ' off');
          return 'Add 1 product to unlock ' + fixedOffer;
        }
      }

      return '';
    };

    PdpInstantBundle.prototype.animateValue = function (elements, from, to) {
      var duration = 350;
      var start = null;
      var self = this;

      function step(timestamp) {
        if (!start) start = timestamp;
        var progress = Math.min((timestamp - start) / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        var current = Math.round(from + (to - from) * eased);
        elements.forEach(function (elm) {
          elm.textContent = formatMoney(current, self.moneyFormat);
        });
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      }

      if (elements.length) window.requestAnimationFrame(step);
    };

    // Only meaningful for discountType 'bxgy': shows the FREE badge (see
    // data-role="free-badge" in the card markup) on exactly the cards
    // computeBxgyDiscount picked as free, and hides it everywhere else —
    // re-run on every recalculate() so it stays correct as products are
    // selected/deselected or a variant swap changes which cards are
    // cheapest. No-op badge text is pre-rendered in Liquid; this only
    // ever toggles visibility.
    PdpInstantBundle.prototype.updateFreeBadges = function (freeVariantIds) {
      var isBxgy = this.discountType === 'bxgy';
      this.cards.forEach(function (card) {
        var badge = card.querySelector('[data-role="free-badge"]');
        if (!badge) return;
        badge.hidden = !(isBxgy && freeVariantIds.indexOf(card.dataset.variantId) !== -1);
      });
    };

    PdpInstantBundle.prototype.recalculate = function (skipAnimation) {
      var self = this;
      var selected = this.getSelectedCards();
      // The main product is a bundle item like any other now, so
      // "addon" cards (used for discount-requires-addon / tier math,
      // which is deliberately scoped to recommended products only) are
      // whatever's selected minus the main card itself.
      var addonCards = selected.filter(function (card) { return card !== self.mainCard; });
      var subtotal = selected.reduce(function (sum, card) {
        return sum + parseInt(card.dataset.price || '0', 10);
      }, 0);
      var itemCount = selected.length;

      var discount = this.computeDiscount(subtotal, itemCount, addonCards.length, selected);
      this.updateFreeBadges(discount.freeVariantIds || []);
      var final = Math.max(subtotal - discount.amount, 0);
      var savingsPercent = subtotal > 0 ? Math.round((discount.amount / subtotal) * 100) : 0;
      Object.keys(this.summaryTargets).forEach(function (key) {
        if (key === 'discountLabel') return;
        var els = self.summaryTargets[key];
        if (!els || !els.length) return;
        if (key === 'subtotal') {
          els.forEach(function (elm) {
            elm.textContent = formatMoney(subtotal, self.moneyFormat);
            // Struck-through MRP once a discount is active, mirroring the
            // cart drawer's original-vs-discounted price treatment.
            elm.classList.toggle('pib-price--struck', discount.amount > 0);
          });
        } else if (key === 'discount') {
          els.forEach(function (elm) { elm.textContent = '-' + formatMoney(discount.amount, self.moneyFormat); });
        } else if (key === 'savingsPercent') {
          els.forEach(function (elm) { elm.textContent = savingsPercent + '%'; });
        } else if (key === 'final') {
          if (!skipAnimation) {
            self.animateValue(els, self.displayedFinal, final);
          } else {
            els.forEach(function (elm) { elm.textContent = formatMoney(final, self.moneyFormat); });
          }
        }
      });

      this.summaryTargets.discountLabel.forEach(function (elm) {
        elm.textContent = discount.label;
      });

      this.savingsRow.forEach(function (row) {
        row.hidden = discount.amount <= 0;
      });

      var availableCount = this.cards.filter(function (card) {
        return card.dataset.available !== 'false';
      }).length;
      var allSelected = availableCount > 0 && selected.length >= availableCount;

      var upsellMessage = allSelected ? '' : this.computeUpsellMessage(itemCount, addonCards.length);

      this.itemCountNumbers.forEach(function (elm) {
        elm.textContent = itemCount;
      });

      // The plain "N products" count now has its own prominent heading
      // (data-bind="item-count-number") and selection state is already
      // visible via the checked cards themselves, so this line only
      // ever needs to speak up for an upsell nudge — otherwise it stays
      // empty rather than adding a redundant status message.
      this.countLabels.forEach(function (elm) {
        if (upsellMessage) {
          elm.textContent = upsellMessage;
          elm.hidden = false;
        } else {
          elm.textContent = '';
          elm.hidden = true;
        }
        elm.classList.toggle('pib-count-label--upsell', !!upsellMessage);
      });

      this.displayedFinal = final;
      this.currentItems = selected.map(function (card) {
        return { id: card.dataset.variantId, quantity: 1 };
      });
      this.currentFinal = final;
      this.updateButtonLabel(itemCount, final);

      var anyUnavailable = selected.some(function (card) {
        return card.dataset.available === 'false';
      });
      var nothingSelected = itemCount === 0;

      this.buyButtons.forEach(function (button) {
        button.disabled = anyUnavailable || nothingSelected || self.isSubmitting;
        button.classList.toggle('pib-buy-button--unavailable', anyUnavailable || nothingSelected);
      });
    };

    PdpInstantBundle.prototype.setStatus = function (message) {
      this.statusRegions.forEach(function (region) {
        region.textContent = message;
      });
    };

    PdpInstantBundle.prototype.buttonTextTemplate = function () {
      return this.ctaMode === 'add_to_cart'
        ? (this.el.dataset.addToCartButtonText || 'Add Bundle to Cart')
        : (this.el.dataset.buttonText || 'Buy Bundle Now');
    };

    // Lets the button communicate the purchase itself (e.g. "Add 4
    // Products • ₹1,098") instead of a generic static label — {count}/
    // {price} in the merchant's configured button text are swapped for
    // the live selection count and computed final price every time
    // recalculate() runs, so it always matches the Final Price row.
    // Templates without those placeholders behave exactly as before.
    // No-ops while a submission is in flight so it can't clobber the
    // "Processing…"/success label set by setSubmitting().
    PdpInstantBundle.prototype.updateButtonLabel = function (itemCount, finalCents) {
      if (this.isSubmitting) return;
      var label = this.buttonTextTemplate()
        .replace(/\{count\}/g, String(itemCount))
        .replace(/\{price\}/g, formatMoney(finalCents, this.moneyFormat));

      this.buyButtons.forEach(function (button) {
        var labelEl = button.querySelector('[data-role="buy-button-label"]');
        if (labelEl) labelEl.textContent = label;
      });
    };

    PdpInstantBundle.prototype.setSubmitting = function (submitting) {
      this.isSubmitting = submitting;
      var processingText = this.el.dataset.buttonProcessingText || 'Processing...';

      this.buyButtons.forEach(function (button) {
        button.disabled = submitting;
        button.setAttribute('aria-busy', submitting ? 'true' : 'false');
        if (submitting) {
          var label = button.querySelector('[data-role="buy-button-label"]');
          if (label) label.textContent = processingText;
        }
        button.classList.toggle('pib-buy-button--loading', submitting);
      });

      if (!submitting) {
        this.updateButtonLabel(this.currentItems ? this.currentItems.length : 0, this.currentFinal || 0);
      }
    };

    PdpInstantBundle.prototype.showSuccess = function () {
      this.buyButtons.forEach(function (button) {
        button.classList.add('pib-buy-button--success');
      });
    };

    PdpInstantBundle.prototype.hideSuccess = function () {
      this.buyButtons.forEach(function (button) {
        button.classList.remove('pib-buy-button--success');
      });
    };

    PdpInstantBundle.prototype.handleCtaClick = function (event) {
      if (this.isSubmitting) return;
      var self = this;
      var isAddToCart = this.ctaMode === 'add_to_cart';
      this.setSubmitting(true);
      this.setStatus('');

      trackEvent(isAddToCart ? 'bundle_add_to_cart' : 'bundle_checkout_started', {
        value: this.currentFinal / 100,
        items: this.currentItems.length,
      });

      var routes = (window.theme && window.theme.routes) || {};
      var cartUrl = routes.cart_url ? routes.cart_url.replace(/\/$/, '') : '/cart';
      var cartAddUrl = routes.cart_add_url || '/cart/add.js';

      var addItems = function () {
        return fetchWithTimeout(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: self.currentItems }),
        }, 10000);
      };

      var request = isAddToCart
        ? addItems()
        : fetchWithTimeout(cartUrl + '/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } }, 10000).then(addItems);

      request
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (data) {
              throw new Error((data && data.description) || 'Unable to add items to cart.');
            });
          }
          return response.json();
        })
        .then(function () {
          if (isAddToCart) {
            // The drawer/toast pop-up should appear the instant the add
            // succeeds, not after an artificial 450ms pause. Skipping
            // showSuccess()'s button checkmark flash here too - showing
            // then immediately hiding it in the same tick (no delay left
            // to separate them) would never actually paint, so it was
            // dead weight; the pop-up itself is the success indicator now.
            self.setSubmitting(false);
            self.refreshCartAfterAdd();
            return;
          }

          self.showSuccess();
          return new Promise(function (resolve) {
            window.setTimeout(resolve, 450);
          });
        })
        .then(function () {
          if (isAddToCart) return;

          if (self.isShiprocketActive()) {
            self.setSubmitting(false);
            self.hideSuccess();
            window.shiprocketCheckoutEvents.buyCart(event);
            return;
          }

          var checkoutUrl = '/checkout';
          if (self.discountCode) {
            checkoutUrl += '?discount=' + encodeURIComponent(self.discountCode);
          }
          window.location.href = checkoutUrl;
        })
        .catch(function () {
          self.setSubmitting(false);
          self.hideSuccess();
          self.setStatus(self.el.dataset.errorText || 'Unable to start checkout. Please try again.');
        });
    };

    /*
     * Refreshes the cart count badge and the cart-drawer section's HTML,
     * then opens it — matching every other Add to Cart entry point in
     * the theme (see combo-collection-by-concern.js's own
     * refreshCartDrawer(), which this deliberately mirrors instead of
     * inventing a new sequencing).
     *
     * History: an earlier version of this method never called .show()
     * at all, working around a scroll-lock bug. A later attempt added a
     * hide()-then-swap-then-show() sequence to fix that safely, but that
     * version froze the page in testing. Rather than re-guess at the
     * exact failure and re-risk another freeze, this now follows the
     * theme's own already-working pattern for the same job (refresh the
     * cart-drawer section, then open it): swap first, then open the
     * fresh node — no hide()/await step, since the fresh node the swap
     * produces always starts closed regardless of the old node's state
     * (the section's Liquid always renders <cart-drawer ... hidden>), so
     * there's nothing stale to release first.
     */
    PdpInstantBundle.prototype.refreshCartAfterAdd = function () {
      var drawerSectionWrapper = document.querySelector('.shopify-section.devx-cart-drawer');
      var sectionId = drawerSectionWrapper ? drawerSectionWrapper.id.replace('shopify-section-', '') : '';

      // Updates every <cart-count class="count"> badge on the page (header
      // icon, mobile-dock bottom nav — both render the same CartCount
      // element, see theme.js). Retries internally (fetchJsonWithRetry)
      // past the 429 bursts other cart-polling widgets on this page cause.
      function syncCartCountBadges() {
        fetchJsonWithRetry('/cart.js', undefined, 4, 500)
          .then(function (cart) {
            document.querySelectorAll('cart-count.count').forEach(function (el) {
              el.textContent = cart.item_count;
              el.hidden = cart.item_count === 0;
            });
            // Other theme widgets (sticky mini cart, quantity selectors,
            // combo-collection cards) listen for this to stay in sync with
            // whatever just changed the cart.
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          })
          .catch(function () {});
      }

      syncCartCountBadges();

      if (!sectionId) return;

      fetchJsonWithRetry(window.location.pathname + '?sections=' + sectionId, undefined, 4, 500)
        .then(function (sectionData) {
          var updatedHtml = sectionData[sectionId];
          var currentSection = document.getElementById('shopify-section-' + sectionId);
          if (updatedHtml && currentSection) {
            currentSection.outerHTML = updatedHtml;
          }

          var drawerEl = document.querySelector('cart-drawer#CartDrawer');
          if (!drawerEl) return;
          drawerEl.removeAttribute('hidden');
          drawerEl.removeAttribute('inert');
          if (typeof drawerEl.show === 'function' && !drawerEl.open) {
            drawerEl.show();
          } else {
            drawerEl.setAttribute('open', '');
            drawerEl.setAttribute('active', '');
          }
          if (window.innerWidth < 768) {
            document.documentElement.classList.add('menu-opened');
            document.body.classList.add('has-modal-open');
          }
        })
        .catch(function () {});
    };

    return PdpInstantBundle;
  })();

  function init() {
    var sections = document.querySelectorAll('pdp-instant-bundle');
    sections.forEach(function (el) {
      if (el.__pibInitialized) return;
      el.__pibInitialized = true;
      new PdpInstantBundle(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
